/**
 * What is inside a log entry, and inside an ephemeral.
 *
 * The server stores and re-serves a payload as the base64 it arrived as and
 * never decodes one. So this schema is the *client's*, shared between the Qt
 * app and this one by agreement rather than by code: its statement of record is
 * `relay/src/data/relaydatasource.h` § The wire, and the encoders around lines
 * 1571 and 1765 of `relaydatasource.cpp` are what actually writes it.
 *
 *     msg      {text, n, rt?, q?, f?, mn?}   a message
 *     callend  {id, o, d?, cam, n}      what a finished call left behind
 *     meeting  {id, title, at?, by}     the transcript card for a meeting
 *     rct      {mid, e, on}             a reaction — parsed, not yet drawn
 *     edit     {mid, text}              a body replaced by its author
 *     del      {mid}                    a message removed by its author
 *
 * **Nothing here escapes anything.** `text`, a quote's `n` and `x`, and an
 * attachment's `x` are a stranger's text and stay a stranger's text: they are
 * rendered as a React string child, which escapes at the point of drawing. The
 * Qt client escapes at this boundary because its delegate draws rich text and
 * has nowhere later to do it; copying that here would be a second escaping with
 * nothing to undo it, and "Q&A" would reach the screen as "Q&amp;A" — which has
 * already shipped once on the desktop, twice.
 *
 * **`mn` is who a message mentions**: account ids, the people the composer's
 * picker recorded whose `@Name` is still in the text at send time. `text`
 * keeps the visible `@Name` inline and stays plain text, so a client that has
 * never heard of `mn` shows the words and loses only the pill. A name in the
 * text is never a mention on its own — only an id in `mn` is.
 *
 * **Authorship is `entry.sender`, never `n`.** A modified client can put any
 * name in the payload; it cannot put another account in the server's stamp. `n`
 * is a display name and is used only to fill in somebody the directory has not
 * answered for yet.
 */

import { decodeBase64, encodeBase64, byteLength, LIMITS } from './frames';
import type { LogEntry } from './frames';
import type {
  Attachment, CallFrame, CallOutcome, Message, Presence, Quote
} from './types';

/** Where a Cloud share link lives. Absolute because Cloud is another product on
 *  another origin, like the accounts service's avatars — it is an href a person
 *  clicks, not a fetch, so it does not touch this app's same-origin rule. */
const CLOUD_ORIGIN = 'https://cloud.mauvely.com';

// ── the shapes ──────────────────────────────────────────────────────────────

/** A quoted message: its id, its author's display name, and one plain line. */
export interface WireQuote {
  mid: string;
  n?: string;
  x?: string;
}

/** One Cloud attachment. The bytes are never here. */
export interface WireFile {
  /** The Cloud node. */
  id: string;
  /** The share link's 32-byte secret, base64url. */
  s?: string;
  /** The link row, so the sender can revoke it. */
  l?: string;
  /** The revision at share time. */
  r?: string;
  /** The filename. */
  x?: string;
  m?: string;
  b?: number;
}

export interface MsgPayload {
  v: 1;
  t: 'msg';
  text: string;
  n?: string;
  /** The message a reply answers. Omitted for a transcript message. */
  rt?: string;
  q?: WireQuote;
  f?: WireFile[];
  /** Account ids the message mentions. Omitted when it mentions nobody. */
  mn?: string[];
}

export interface CallEndPayload {
  v: 1;
  t: 'callend';
  id: string;
  o: CallOutcome;
  /** Seconds, present only when answered. */
  d?: number;
  cam?: boolean;
  n?: string;
}

/**
 * The meeting card, per `relay-server/docs/calls-and-meetings.md` Part 2:
 * `{v:1, t:'meeting', id, title, at?, by}`, posted by the creator once. Client
 * posted, like `callend` — relay-server knows about neither.
 */
export interface MeetingPayload {
  v: 1;
  t: 'meeting';
  id: string;
  title: string;
  /** Milliseconds. Absent for a meeting with no time set. */
  at?: number;
  by?: string;
}

/** Anything else with a `t`: a kind a newer build invented, or one this app
 *  does not draw yet. Skipped, which is the posture the log already takes. */
export interface OtherPayload {
  v?: number;
  t: string;
  [key: string]: unknown;
}

export type WirePayload = MsgPayload | CallEndPayload | MeetingPayload | OtherPayload;

// ── decoding ────────────────────────────────────────────────────────────────

function asObject(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/**
 * A base64 payload as a wire object, or null.
 *
 * Null is ordinary: the log kept its MLS-era rows, whose payloads are
 * ciphertext and parse as nothing. Tolerating them is what let the model change
 * without a wire version bump.
 */
export function decodePayload(base64: string): WirePayload | null {
  const text = decodeBase64(base64);
  if (text === null) return null;
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return null;
  }
  const obj = asObject(value);
  if (!obj || typeof obj['t'] !== 'string') return null;
  return obj as WirePayload;
}

/** The display name the payload carries, for a sender the directory has not
 *  answered for yet. Display only. */
export function payloadName(payload: WirePayload): string {
  return str((payload as OtherPayload)['n']);
}

// The shapes Cloud mints: a link id, and 32 bytes as base64url. Both go into an
// address on Cloud's origin, so anything else (`../authorise`) opens nothing.
const LINK_ID = /^[A-Za-z0-9_-]{1,64}$/;
const LINK_SECRET = /^[A-Za-z0-9_-]{43}$/;

function attachmentsOf(payload: MsgPayload): Attachment[] {
  const files = Array.isArray(payload.f) ? payload.f : [];
  const out: Attachment[] = [];
  for (const raw of files) {
    const file = asObject(raw);
    if (!file) continue;
    const nodeId = str(file['id']);
    if (!nodeId) continue;
    const linkId = str(file['l']);
    const secret = str(file['s']);
    out.push({
      nodeId,
      name: str(file['x']),
      mime: str(file['m']),
      bytes: num(file['b']),
      // A link needs both halves. Without them the card still draws — the name
      // and the size are what it is mostly made of — and there is nothing to
      // open.
      cloudUrl:
        LINK_ID.test(linkId) && LINK_SECRET.test(secret) ? `${CLOUD_ORIGIN}/s/${linkId}#${secret}` : ''
    });
  }
  return out;
}

function quoteOf(payload: MsgPayload): Quote | undefined {
  const quote = asObject(payload.q);
  if (!quote) return undefined;
  const messageId = str(quote['mid']);
  if (!messageId) return undefined;
  // Both fields are sender-supplied and display only. `senderId` is the whole
  // of authorship.
  return { messageId, author: str(quote['n']), excerpt: str(quote['x']) };
}

/** The most people one message can mention. Both ends hold to it. */
export const MAX_MENTIONS = 50;

/** relay-server's own rule for an account id (`protocol.ts`). */
const ACCOUNT_ID = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * `mn` as a clean list: strings shaped like an account id, each once, at most
 * `MAX_MENTIONS`. The encoder runs the same filter, so what goes out is what a
 * reader keeps.
 */
export function mentionsOf(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const id of value) {
    if (out.length === MAX_MENTIONS) break;
    if (typeof id === 'string' && ACCOUNT_ID.test(id) && !out.includes(id)) out.push(id);
  }
  return out;
}

const OUTCOMES: CallOutcome[] = ['answered', 'missed', 'declined'];

/**
 * A log entry as a transcript row, or null when there is nothing to draw.
 *
 * Null for the MLS-era kinds, for a payload that is not a wire object, and for
 * `rct` / `edit` / `del` — which are real and change a message this app already
 * holds rather than adding one. The store applies those itself; this function
 * answers the one question "is this a row?".
 */
export function decodeEntry(
  conversationId: string,
  entry: LogEntry,
  /** The already-decoded payload, when the caller needed it for something else
   *  — the store reads `n` off it before this runs. Saves opening the same
   *  base64 twice per entry on a cold sync of a long channel. */
  decoded?: WirePayload
): Message | null {
  if (entry.kind !== 'application') return null;
  const payload = decoded ?? decodePayload(entry.payload);
  if (!payload) return null;

  const base = {
    id: entry.id,
    conversationId,
    seq: entry.seq,
    senderId: entry.sender,
    at: entry.at,
    attachments: [] as Attachment[],
    // The server sends this to the sender alone, so there is nothing to check
    // here about whose message it is.
    ...(entry.unreachable?.length ? { undeliveredTo: entry.unreachable } : {})
  };

  if (payload.t === 'msg') {
    const msg = payload as MsgPayload;
    const replyTo = str(msg.rt);
    const quote = quoteOf(msg);
    const mentions = mentionsOf(msg.mn);
    return {
      ...base,
      body: str(msg.text),
      attachments: attachmentsOf(msg),
      kind: 'msg',
      ...(replyTo ? { replyTo } : {}),
      ...(quote ? { quote } : {}),
      ...(mentions.length > 0 ? { mentions } : {})
    };
  }

  if (payload.t === 'callend') {
    const call = payload as CallEndPayload;
    const outcome = OUTCOMES.includes(call.o) ? call.o : 'missed';
    return {
      ...base,
      // No body. The row's words are generated where it is drawn, from the
      // record and from who wrote it — "No answer" and "Missed call" are this
      // one row read from the two seats.
      body: '',
      kind: 'callend',
      call: {
        outcome,
        durationSeconds: Math.max(0, num(call.d)),
        camera: call.cam === true
      }
    };
  }

  if (payload.t === 'meeting') {
    const meeting = payload as MeetingPayload;
    const id = str(meeting.id);
    if (!id) return null;
    const at = num(meeting.at);
    return {
      ...base,
      body: '',
      kind: 'meeting',
      meeting: { id, title: str(meeting.title), ...(at ? { at } : {}) }
    };
  }

  return null;
}

/**
 * An `edit` or a `del`: a change to a message already in the log, by id.
 *
 * Who may make it is not decided here. The payload names a target and nothing
 * else; the receiver holds the target's `senderId` and compares it with the
 * entry's server-stamped sender, which a modified client cannot forge.
 */
export type Amendment =
  | { kind: 'edit'; messageId: string; body: string }
  | { kind: 'del'; messageId: string };

export function amendmentOf(payload: WirePayload): Amendment | null {
  if (payload.t !== 'edit' && payload.t !== 'del') return null;
  const wire = payload as OtherPayload;
  const messageId = str(wire['mid']);
  if (!messageId) return null;
  if (payload.t === 'del') return { kind: 'del', messageId };
  return { kind: 'edit', messageId, body: str(wire['text']) };
}

// ── encoding ────────────────────────────────────────────────────────────────

export interface MsgDraft {
  body: string;
  /** The sender's display name. Display only, everywhere it lands. */
  name: string;
  replyTo?: string;
  quote?: Quote;
  files?: WireFile[];
  mentions?: string[];
}

export function encodeMsg(draft: MsgDraft): string {
  const wire: MsgPayload = { v: 1, t: 'msg', text: draft.body, n: draft.name };
  if (draft.replyTo) wire.rt = draft.replyTo;
  if (draft.quote) {
    wire.q = {
      mid: draft.quote.messageId,
      n: draft.quote.author,
      x: draft.quote.excerpt
    };
  }
  if (draft.files && draft.files.length > 0) wire.f = draft.files;
  const mentions = mentionsOf(draft.mentions);
  if (mentions.length > 0) wire.mn = mentions;
  return encodeBase64(JSON.stringify(wire));
}

export function encodeCallEnd(
  callId: string,
  outcome: CallOutcome,
  opts: { name: string; camera?: boolean; durationSeconds?: number }
): string {
  const wire: CallEndPayload = {
    v: 1,
    t: 'callend',
    id: callId,
    o: outcome,
    cam: opts.camera === true,
    n: opts.name
  };
  // A duration only means something on a call somebody was in. Writing one for
  // a call that rang out would put "0:00" under a conversation that never
  // happened.
  if (outcome === 'answered' && opts.durationSeconds && opts.durationSeconds > 0) {
    wire.d = Math.round(opts.durationSeconds);
  }
  return encodeBase64(JSON.stringify(wire));
}

/**
 * A reaction toggle: `{v:1, t:'rct', mid, e, on}`.
 *
 * No `n`. Every other kind carries the sender's display name for a directory
 * that has not answered yet, and this one deliberately does not — the desktop's
 * encoder does not write one, and a field on one side only is a field that is
 * never read.
 */
export function encodeReaction(messageId: string, emoji: string, on: boolean): string {
  return encodeBase64(JSON.stringify({ v: 1, t: 'rct', mid: messageId, e: emoji, on }));
}

/** A body replaced: `{v:1, t:'edit', mid, text}`. No `n`, as the desktop's
 *  `editMessage` writes none. Only the author's to send, and every receiver
 *  checks that against the server's stamp. */
export function encodeEdit(messageId: string, body: string): string {
  return encodeBase64(JSON.stringify({ v: 1, t: 'edit', mid: messageId, text: body }));
}

/** A message removed: `{v:1, t:'del', mid}`. The same author rule as `edit`. */
export function encodeDelete(messageId: string): string {
  return encodeBase64(JSON.stringify({ v: 1, t: 'del', mid: messageId }));
}

export function encodeMeetingCard(
  meetingId: string,
  title: string,
  opts: { name: string; at?: number }
): string {
  const wire: MeetingPayload = { v: 1, t: 'meeting', id: meetingId, title, by: opts.name };
  if (opts.at) wire.at = opts.at;
  return encodeBase64(JSON.stringify(wire));
}

// ── the ephemeral lane ──────────────────────────────────────────────────────

export interface TypingPayload {
  v: 1;
  t: 'typ';
  on: boolean;
  n?: string;
}

/** The presence integer, which is the desktop's enum and rides the wire as an
 *  int: Available 0, Away 1, Busy 2, Offline 3, DoNotDisturb 4, Unknown 5. */
export interface PresencePayload {
  v: 1;
  t: 'prs';
  p: number;
  n?: string;
}

export type EphemeralPayload = TypingPayload | PresencePayload | CallFrame | OtherPayload;

export const PRESENCE_WIRE = {
  available: 0,
  away: 1,
  busy: 2,
  offline: 3,
  doNotDisturb: 4,
  unknown: 5
} as const;

/**
 * The wire integer as one of this app's four.
 *
 * Do-not-disturb folds onto `busy`: it is a red dot with a bar through it on
 * the desktop and this app draws four states, so the nearest true one is busy.
 * Anything outside the enum is nothing at all — a dot drawn from a number this
 * build does not understand is worse than no dot.
 */
export function presenceFromWire(value: unknown): Presence | null {
  switch (value) {
    case PRESENCE_WIRE.available:
      return 'online';
    case PRESENCE_WIRE.away:
      return 'away';
    case PRESENCE_WIRE.busy:
    case PRESENCE_WIRE.doNotDisturb:
      return 'busy';
    case PRESENCE_WIRE.offline:
      return 'offline';
    default:
      return null;
  }
}

export function presenceToWire(presence: Presence): number {
  switch (presence) {
    case 'online':
      return PRESENCE_WIRE.available;
    case 'away':
      return PRESENCE_WIRE.away;
    case 'busy':
      return PRESENCE_WIRE.busy;
    case 'offline':
      return PRESENCE_WIRE.offline;
  }
}

export function decodeEphemeral(base64: string): EphemeralPayload | null {
  const payload = decodePayload(base64);
  return payload as EphemeralPayload | null;
}

/**
 * An ephemeral, or null when it is too big to send.
 *
 * The cap is fatal rather than a drop: the server computes the decoded length
 * and closes the socket over it. Every kind carries `n`, a display name nothing
 * clamps, so the symptom of not checking here is a connection that dies at a
 * keystroke, for one person, with a long name. Refusing the frame loses a
 * typing dot; sending it loses the session.
 */
export function encodeEphemeral(payload: EphemeralPayload): string | null {
  const text = JSON.stringify(payload);
  if (byteLength(text) > LIMITS.maxEphemeralBytes) return null;
  return encodeBase64(text);
}
